import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight, ArrowLeft, Calendar, Clock, CreditCard, AlertTriangle, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Section, SpecialRequest } from "@/components/common";
import { whatsappUrl, studioBookingMessage } from "@/lib/brand";

const STEPS = ["Prestation", "Date & créneau", "Coordonnées", "Paiement"];
const ALL_DURATIONS = [1, 2, 3, 4, 5, 6, 7, 8];
const FEATURED_CATS = ["Enregistrement", "Mixage", "Mastering"];

function money(n) { return `${Number(n || 0).toFixed(2)} €`; }
function ttcOf(s) { return Number(s?.price || 0) * (1 + Number(s?.vat || 0) / 100); }

export default function BookingStudio() {
  const [step, setStep] = useState(0);
  const [services, setServices] = useState([]);
  const [service, setService] = useState(null);
  const [duration, setDuration] = useState(2);
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slot, setSlot] = useState("");
  const [client, setClient] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [reservation, setReservation] = useState(null);
  const [payInfo, setPayInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  const minDur = service?.min_duration ? Number(service.min_duration) : 1;
  const durations = ALL_DURATIONS.filter((d) => d >= minDur);
  const isHourly = service?.price_type === "hourly";

  useEffect(() => { if (service) setDuration(Math.max(minDur, service.min_duration || 1)); /* eslint-disable-next-line */ }, [service]);

  useEffect(() => {
    if (step === 1 && date && service) {
      setSlotsLoading(true);
      setSlot("");
      api.get(`/booking/availability/${date}?duration=${duration}&service_id=${service?.id || ""}`)
        .then((r) => setSlots(r.data.slots)).catch(() => setSlots([])).finally(() => setSlotsLoading(false));
    }
  }, [step, date, duration, service]);

  const priceHT = service ? (isHourly ? Number(service.price || 0) * duration : Number(service.price || 0)) : 0;
  const vat = service ? Number(service.vat || 0) : 0;
  const priceTTC = priceHT * (1 + vat / 100);
  const depositPct = service ? (service.deposit_percent != null ? Number(service.deposit_percent) : (service.price_type === "day" ? 50 : 100)) : 100;
  const isFull = depositPct >= 100;
  const depositAmount = Math.round(priceTTC * depositPct / 100 * 100) / 100;
  const balance = Math.round((priceTTC - depositAmount) * 100) / 100;

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const createReservation = async () => {
    setLoading(true);
    try {
      const res = await api.post("/booking/reservations", {
        service_id: service.id, date, start_time: slot, duration,
        first_name: client.first_name, last_name: client.last_name, email: client.email, phone: client.phone,
      });
      setReservation(res.data);
      const pay = await api.post(`/booking/reservations/${res.data.id}/pay`);
      setPayInfo(pay.data);
      if (pay.data.payment_enabled && pay.data.checkout_url) {
        window.location.assign(pay.data.checkout_url);
      }
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const today = new Date().toISOString().split("T")[0];
  const waMsg = studioBookingMessage({
    nom: `${client.first_name} ${client.last_name}`.trim(), date, heure: slot,
    prestation: service?.name || "", duree: isHourly ? `${duration}h` : "", tarif: money(priceTTC),
  });

  const featured = services.filter((s) => FEATURED_CATS.includes(s.category));
  const others = services.filter((s) => !FEATURED_CATS.includes(s.category));

  const ServiceCard = (s) => {
    const hourly = s.price_type === "hourly";
    return (
      <button key={s.id} data-testid={`booking-service-${s.id}`} onClick={() => setService(s)} className={`card-dark p-6 text-left transition-colors ${service?.id === s.id ? "!border-amber-400 ring-1 ring-amber-400/40" : ""}`}>
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display font-bold text-lg">{s.name}</h3>
          {service?.id === s.id && <Check size={18} className="text-gold shrink-0 mt-1" />}
        </div>
        {s.conditions && <p className="text-xs text-amber-400 mt-1">{s.conditions}</p>}
        {s.description && <p className="text-sm text-slate-400 mt-2 line-clamp-3">{s.description}</p>}
        {s.quote_note && <p className="text-xs text-slate-500 mt-2 italic">{s.quote_note}</p>}
        <div className="mt-4">
          <span className="text-gold font-mono text-sm">{money(s.price)} HT{hourly ? " / h" : ""}</span>
          <span className="text-slate-400 font-mono text-xs ml-2">{money(ttcOf(s))} TTC{hourly ? " / h" : ""}</span>
          {hourly && <span className="text-amber-400 text-xs ml-2">· min {s.min_duration || 2}h</span>}
        </div>
      </button>
    );
  };

  return (
    <div data-testid="booking-studio-page" className="min-h-screen">
      <div className="relative border-b border-white/10 py-14">
        <Section>
          <div className="eyebrow mb-3">Réservation studio</div>
          <h1 className="font-display font-black uppercase text-3xl sm:text-5xl">Réserve ta session</h1>
          <p className="text-slate-400 mt-3">Simple et rapide : choisis ta prestation, ta date et ton créneau, renseigne tes coordonnées, puis paie en ligne pour confirmer.</p>
        </Section>
      </div>

      <Section className="py-10 no-print">
        <div className="flex items-center gap-2 mb-10 overflow-x-auto pb-2">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2 shrink-0">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border ${i === step ? "bg-gold text-black border-transparent" : i < step ? "border-amber-400/40 text-gold" : "border-white/10 text-slate-500"}`}>
                {i < step ? <Check size={13} /> : <span>{i + 1}</span>} <span>{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`w-5 h-px ${i < step ? "bg-amber-400/40" : "bg-white/10"}`} />}
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-[1fr_340px] gap-8">
          <div>
            <AnimatePresence mode="wait">
              <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>

                {step === 0 && (
                  <div>
                    <h2 className="font-display font-bold text-2xl mb-6">1. Choisis ta prestation</h2>
                    {services.length === 0 ? (
                      <div className="card-dark p-8 text-center text-slate-500 text-sm">Aucune prestation disponible pour le moment.</div>
                    ) : (
                      <>
                        {featured.length > 0 && (
                          <div className="mb-8">
                            <div className="eyebrow mb-3">Les plus demandés — Réserve ton studio · Mixe · Masterise</div>
                            <div className="grid sm:grid-cols-2 gap-4">
                              {featured.map(ServiceCard)}
                            </div>
                          </div>
                        )}
                        {others.length > 0 && (
                          <div>
                            <div className="eyebrow mb-3">Autres prestations</div>
                            <div className="grid sm:grid-cols-2 gap-4">
                              {others.map(ServiceCard)}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {service && isHourly && (
                      <div className="mt-8">
                        <h3 className="font-display font-bold text-lg mb-1">Durée de la session</h3>
                        {minDur > 1 && <p className="text-sm text-amber-400 mb-4">Minimum {minDur}h pour cette prestation. Prix calculé automatiquement.</p>}
                        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mt-3">
                          {durations.map((d) => (
                            <button key={d} data-testid={`booking-duration-${d}`} onClick={() => setDuration(d)} className={`card-dark py-5 text-center ${duration === d ? "!border-amber-400 text-gold" : ""}`}>
                              <div className="font-display font-black text-xl">{d}h</div>
                              <div className="text-[11px] text-slate-400 mt-1">{money(Number(service.price || 0) * d * (1 + vat / 100))} TTC</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-3 mt-8">
                      <button onClick={next} disabled={!service} data-testid="booking-step0-next" className="btn-gold">Continuer <ArrowRight size={16} /></button>
                    </div>
                  </div>
                )}

                {step === 1 && (
                  <div>
                    <h2 className="font-display font-bold text-2xl mb-6">2. Date & créneau</h2>
                    <label className="label-dark">Date souhaitée</label>
                    <input type="date" data-testid="booking-date-input" min={today} value={date} onChange={(e) => setDate(e.target.value)} className="input-dark max-w-xs" />

                    {date && (
                      <div className="mt-8">
                        <label className="label-dark">Créneaux disponibles</label>
                        {slotsLoading ? (
                          <div className="flex items-center gap-2 text-slate-400 mt-2"><Loader2 className="animate-spin" size={18} /> Chargement…</div>
                        ) : slots.length === 0 ? (
                          <div className="card-dark p-6 text-center text-slate-500 text-sm mt-2">Aucun créneau disponible pour cette date. Essaie une autre date.</div>
                        ) : (
                          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 mt-2">
                            {slots.map((s) => (
                              <button key={s} data-testid={`booking-slot-${s}`} onClick={() => setSlot(s)} className={`card-dark py-4 text-center font-mono ${slot === s ? "!border-amber-400 text-gold" : ""}`}>{s}</button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex gap-3 mt-8">
                      <button onClick={back} className="btn-ghost"><ArrowLeft size={16} /> Retour</button>
                      <button onClick={next} disabled={!slot} data-testid="booking-step1-next" className="btn-gold">Continuer <ArrowRight size={16} /></button>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div>
                    <h2 className="font-display font-bold text-2xl mb-6">3. Tes coordonnées</h2>
                    <div className="grid sm:grid-cols-2 gap-5 max-w-xl">
                      <div><label className="label-dark">Prénom *</label><input data-testid="booking-first-name" className="input-dark" value={client.first_name} onChange={(e) => setClient({ ...client, first_name: e.target.value })} /></div>
                      <div><label className="label-dark">Nom *</label><input data-testid="booking-last-name" className="input-dark" value={client.last_name} onChange={(e) => setClient({ ...client, last_name: e.target.value })} /></div>
                      <div><label className="label-dark">Email *</label><input type="email" data-testid="booking-email" className="input-dark" value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} /></div>
                      <div><label className="label-dark">Téléphone *</label><input data-testid="booking-phone" className="input-dark" value={client.phone} onChange={(e) => setClient({ ...client, phone: e.target.value })} /></div>
                    </div>
                    <div className="flex gap-3 mt-8">
                      <button onClick={back} className="btn-ghost"><ArrowLeft size={16} /> Retour</button>
                      <button onClick={next} disabled={!client.first_name || !client.last_name || !client.email || !client.phone} data-testid="booking-step2-next" className="btn-gold">Continuer <ArrowRight size={16} /></button>
                    </div>
                  </div>
                )}

                {step === 3 && !reservation && (
                  <div>
                    <h2 className="font-display font-bold text-2xl mb-6">4. Récapitulatif & paiement</h2>
                    <div className="card-dark p-6 space-y-3 max-w-xl">
                      <Row label="Prestation" value={service?.name} />
                      <Row label="Date" value={date} />
                      <Row label="Heure" value={slot} />
                      {isHourly && <Row label="Durée" value={`${duration} h`} />}
                      <div className="border-t border-white/10 my-2" />
                      {vat > 0 && <><Row label="Prix HT" value={money(priceHT)} /><Row label={`TVA (${vat}%)`} value={money(priceTTC - priceHT)} /></>}
                      <Row label="Prix total TTC" value={money(priceTTC)} gold />
                      <Row label={isFull ? "Montant à payer (100 %)" : `Acompte à payer (${depositPct} %)`} value={money(depositAmount)} />
                      {!isFull && <Row label="Solde restant (au studio)" value={money(balance)} />}
                      <div className="border-t border-white/10 my-2" />
                      <Row label="Client" value={`${client.first_name} ${client.last_name}`} />
                      <Row label="Email" value={client.email} />
                      <Row label="Téléphone" value={client.phone} />
                    </div>
                    <p className="text-xs text-slate-500 mt-3 max-w-xl">{isFull ? "Le paiement intégral bloque définitivement ton créneau." : "Le versement de l'acompte bloque définitivement ton créneau. Le solde est réglé au studio."}</p>
                    <div className="flex flex-wrap gap-3 mt-8">
                      <button onClick={back} className="btn-ghost"><ArrowLeft size={16} /> Retour</button>
                      <button onClick={createReservation} disabled={loading} data-testid="booking-confirm-reservation" className="btn-gold">{loading ? <><Loader2 className="animate-spin" size={16} /> …</> : <>{isFull ? "Payer et confirmer" : "Payer l'acompte et confirmer"} <CreditCard size={16} /></>}</button>
                    </div>
                  </div>
                )}

                {step === 3 && reservation && (
                  <div>
                    <h2 className="font-display font-bold text-2xl mb-6">Paiement</h2>
                    <div className="card-dark p-6 max-w-xl">
                      <div className="flex items-center gap-2 text-xs font-mono text-slate-400">Réservation <span className="text-gold">{reservation.booking_number}</span></div>
                      <div className="mt-2 flex items-baseline gap-3">
                        <span className="text-2xl font-display font-black">{money(reservation.deposit_amount)}</span>
                        <span className="text-sm text-slate-500">{reservation.deposit_amount >= reservation.price_ttc ? "à payer" : `acompte / ${money(reservation.price_ttc)} total`}</span>
                      </div>
                      {payInfo && !payInfo.payment_enabled ? (
                        <div className="mt-6 rounded-xl border border-amber-400/30 bg-amber-400/5 p-5">
                          <div className="flex items-start gap-3">
                            <AlertTriangle className="text-gold shrink-0 mt-0.5" size={20} />
                            <div className="text-sm text-slate-300">
                              <p className="font-semibold text-gold">Paiement en ligne temporairement indisponible</p>
                              <p className="mt-2">{payInfo.message}</p>
                              <p className="mt-3 text-slate-400">Ton créneau est <strong>réservé temporairement</strong>. Contacte-nous pour finaliser :</p>
                              <a href={whatsappUrl(waMsg)} target="_blank" rel="noreferrer" data-testid="booking-whatsapp-pay" className="btn-gold mt-4 !py-2 text-xs inline-flex"><MessageCircle size={14} /> Nous écrire sur WhatsApp</a>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-6 flex items-center gap-2 text-sm text-slate-400"><Loader2 className="animate-spin" size={16} /> Redirection vers le paiement sécurisé SumUp…</div>
                      )}
                      <p className="text-xs text-slate-600 mt-4">Statut : <span className="font-mono">{reservation.status}</span></p>
                    </div>
                  </div>
                )}

              </motion.div>
            </AnimatePresence>
          </div>

          <div className="hidden lg:block">
            <div className="card-dark p-6 sticky top-24">
              <div className="eyebrow mb-4">Ta session</div>
              <RecapLine label="Prestation" value={service?.name || "—"} />
              {isHourly && <RecapLine icon={<Clock size={14} />} label="Durée" value={`${duration} h`} />}
              <RecapLine icon={<Calendar size={14} />} label="Date" value={date || "—"} />
              <RecapLine icon={<Clock size={14} />} label="Créneau" value={slot || "—"} />
              <div className="border-t border-white/10 my-4" />
              {vat > 0 && service && <div className="flex justify-between text-xs text-slate-500 mt-1"><span>Total HT</span><span>{money(priceHT)}</span></div>}
              <div className="flex justify-between mt-1 font-display font-black text-xl"><span>Total TTC</span><span className="text-gold">{money(priceTTC)}</span></div>
              <div className="flex justify-between text-sm text-slate-300 mt-2"><span>{isFull ? "À payer" : `Acompte (${depositPct}%)`}</span><span>{money(depositAmount)}</span></div>
              {!isFull && <div className="flex justify-between text-sm text-slate-500 mt-1"><span>Solde au studio</span><span>{money(balance)}</span></div>}
            </div>
          </div>
        </div>
      </Section>

      <SpecialRequest />
    </div>
  );
}

function Row({ label, value, gold }) {
  return <div className="flex justify-between text-sm"><span className="text-slate-500">{label}</span><span className={gold ? "text-gold font-bold font-display text-lg" : "text-white"}>{value}</span></div>;
}
function RecapLine({ icon, label, value }) {
  return <div className="flex items-center justify-between py-1.5 text-sm"><span className="text-slate-500 flex items-center gap-1.5">{icon}{label}</span><span className="text-white text-right max-w-[55%] truncate">{value}</span></div>;
}
